import { useState } from "react";
import { X, Calculator } from "lucide-react";
import { useCurrency } from "@/contexts/currency";
import { formatPrice } from "@/config/pricing";
import { useAuth } from "@/auth/AuthProvider";

interface PricingCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingCalculator = ({ isOpen, onClose }: PricingCalculatorProps) => {
  const [numGenies, setNumGenies] = useState(1);
  const [callsPerGenie, setCallsPerGenie] = useState(30);
  const [phoneNumbers, setPhoneNumbers] = useState({
    local: false,
    tollFree: false,
  });

  const { currency, currencyConfig } = useCurrency();
  const { user } = useAuth();

  // Dynamic pricing from currency config
  const GENIE_PRICE = currencyConfig.displayPrices.professional;
  const CALL_OVERAGE = currencyConfig.displayPrices.additionalCall;
  const INCLUDED_CALLS = 30;
  const PHONE_PRICES = {
    local: currencyConfig.displayPrices.phoneLocal,
    tollFree: currencyConfig.displayPrices.phoneTollFree,
  };

  // Calculations
  const geniesTotal = numGenies * GENIE_PRICE;
  const totalCallsIncluded = numGenies * INCLUDED_CALLS;
  const totalCalls = numGenies * callsPerGenie;
  const additionalCalls = Math.max(0, totalCalls - totalCallsIncluded);
  const callsTotal = additionalCalls * CALL_OVERAGE;

  const phoneTotal =
    (phoneNumbers.local ? PHONE_PRICES.local : 0) +
    (phoneNumbers.tollFree ? PHONE_PRICES.tollFree : 0);

  const monthlyRecurring = geniesTotal + callsTotal + phoneTotal;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} />

      {/* Side Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[720px] bg-white shadow-2xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Pricing Calculator</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-2 rounded-lg transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Number of Genies */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-sm font-semibold text-gray-900">
                How many Genies do you need?
              </label>
              <span className="text-xs text-gray-500">
                {formatPrice(GENIE_PRICE, currency)}/mo • {numGenies * INCLUDED_CALLS} calls included
              </span>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="10"
                value={numGenies}
                onChange={(e) => setNumGenies(Number(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <input
                type="number"
                min="1"
                value={numGenies}
                onChange={(e) => setNumGenies(Math.max(1, Number(e.target.value)))}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold"
              />
            </div>
          </div>

          {/* Calls per Genie */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-sm font-semibold text-gray-900">
                Expected calls per genie per month
              </label>
              {additionalCalls > 0 && (
                <span className="text-xs text-gray-500">
                  {additionalCalls} extra × {formatPrice(CALL_OVERAGE, currency)} = {formatPrice(callsTotal, currency)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="500"
                step="10"
                value={callsPerGenie}
                onChange={(e) => setCallsPerGenie(Number(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <input
                type="number"
                min="0"
                value={callsPerGenie}
                onChange={(e) => setCallsPerGenie(Math.max(0, Number(e.target.value)))}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold"
              />
            </div>
          </div>

          {/* Phone Numbers */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Phone numbers needed</label>
            <div className="space-y-1">
              <label className="flex items-center justify-between p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <div>
                  <span className="text-sm font-medium text-gray-900">Local Number</span>
                  <span className="text-xs text-gray-500 ml-1.5">{formatPrice(PHONE_PRICES.local, currency)}/mo</span>
                </div>
                <input
                  type="checkbox"
                  checked={phoneNumbers.local}
                  onChange={(e) => setPhoneNumbers({ ...phoneNumbers, local: e.target.checked })}
                  className="w-4 h-4 accent-purple-600"
                />
              </label>

              <label className="flex items-center justify-between p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <div>
                  <span className="text-sm font-medium text-gray-900">Toll-Free Number</span>
                  <span className="text-xs text-gray-500 ml-1.5">{formatPrice(PHONE_PRICES.tollFree, currency)}/mo</span>
                </div>
                <input
                  type="checkbox"
                  checked={phoneNumbers.tollFree}
                  onChange={(e) => setPhoneNumbers({ ...phoneNumbers, tollFree: e.target.checked })}
                  className="w-4 h-4 accent-purple-600"
                />
              </label>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border-2 border-indigo-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Your Estimated Costs</h3>

            {/* Monthly Recurring */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-base font-semibold text-purple-900">Monthly (Ongoing):</span>
                <span className="text-3xl font-bold text-purple-900">{formatPrice(monthlyRecurring, currency)}</span>
              </div>
              <div className="space-y-1 text-sm text-gray-600 pl-2 border-l-2 border-purple-200">
                <div className="flex justify-between">
                  <span>Genies ({numGenies}×)</span>
                  <span>{formatPrice(geniesTotal, currency)}</span>
                </div>
                {callsTotal > 0 && (
                  <div className="flex justify-between">
                    <span>Additional calls ({additionalCalls}×)</span>
                    <span>{formatPrice(callsTotal, currency)}</span>
                  </div>
                )}
                {phoneTotal > 0 && (
                  <div className="flex justify-between">
                    <span>Phone numbers</span>
                    <span>{formatPrice(phoneTotal, currency)}</span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* CTA */}
          <div className="space-y-3">
            <button
              onClick={() => {
                if (user) {
                  onClose();
                } else {
                  window.location.href = "/signup";
                }
              }}
              className="block w-full text-center py-3 px-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
            >
              Get Started with Professional
            </button>
            <p className="text-xs text-gray-500 text-center">
              Need more than 10 genies or custom requirements?{" "}
              <a href="/live-agent/business-onboarding" className="text-purple-600 hover:underline font-medium">
                Contact Sales
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingCalculator;
